import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Spinner } from "@chakra-ui/react";
import { StepContainer, Step } from "newui/components/Stepper";
import {
	EVALUATION_STEP_META,
	EVALUATION_STEP_IDS,
	resolveStepDescription,
} from "../../constants/evaluation.constants";
import { isSoloPanelMode } from "../../utils/panelModes";
import { PreComplianceStep } from "../steps/PreComplianceStep";
import { Stage2ComplianceGrid } from "../steps/PreComplianceStep/Stage2ComplianceGrid";
import { EvaluationCriteriaStep } from "../steps/EvaluationCriteriaStep";
import { EvaluationPanelStep } from "../steps/EvaluationPanelStep";
import { InitialEvaluationStep } from "../steps/InitialEvaluationStep";
import { ClarificationsStep } from "../steps/ClarificationsStep";
import { RevisionsStep } from "../steps/RevisionsStep";
import { FinalEvaluationStep } from "../steps/FinalEvaluationStep";
import { FinalReportAndAwardStep } from "../steps/FinalReportAndAwardStep";
import type { EvaluationState } from "../../types/evaluation.type";
import type { PanelMode } from "../../types/panel.type";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import { useComplianceStatus, complianceKeys } from "../../hooks/useCompliance";
import type { PreComplianceCompletionSource } from "../steps/PreComplianceStep";

interface RFxEvaluationStepsProps {
	evaluationState: EvaluationState | null;
	isRfxLead?: boolean;
	rfxData: Rfx;
	rfxSubmissionId?: number;
	onAdvanceStep?: () => Promise<EvaluationState | null>;
	onComplete?: () => void;
	onPanelModeChange?: (mode: PanelMode) => void;
	onRefreshUserContext?: () => void;
	onStepChange?: (step: number) => void;
}

const ALL_STEP_IDS = [0, 1, 2, 3, 4, 5, 6, 7] as const;

/**
 * Maps a backend step index (0-based into the full 8-step array) to the
 * corresponding index in the filtered/visible steps array.
 *
 * For example, in solo mode (steps [1,3,4,5,6,7]):
 *   backend step 0 (Criteria, id=1) -> filtered index 0
 *   backend step 2 (Initial, id=3) -> filtered index 1
 *   backend step 3 (Clarifications, id=4) -> filtered index 2
 */
export const mapBackendStepToFilteredIndex = (
	backendStepIndex: number,
	activeStepIds: readonly number[]
): number => {
	// Sentinel -1 from the backend means we are at Step 0 (Pre-Compliance).
	if (backendStepIndex === -1) {
		const preComplianceIndex = activeStepIds.indexOf(EVALUATION_STEP_IDS.PRE_COMPLIANCE);
		return preComplianceIndex !== -1 ? preComplianceIndex : 0;
	}

	// Convert backend 0-based index to 1-based step ID
	const backendStepId = backendStepIndex + 1;

	// Find this step ID in the filtered list
	const filteredIndex = activeStepIds.indexOf(backendStepId);
	if (filteredIndex !== -1) {
		return filteredIndex;
	}

	// If the exact step is not in the filtered list (e.g., Panel step removed),
	// find the next visible step after it
	const nextVisibleIndex = activeStepIds.findIndex(
		(id) => id > backendStepId
	);
	if (nextVisibleIndex !== -1) {
		return nextVisibleIndex;
	}

	// Fallback to last step
	return Math.max(0, activeStepIds.length - 1);
};

/**
 * Pure guard backing maybeAdvance: returns whether the backend needs to be
 * advanced for the given step, given the backend's current 0-based step value.
 *
 * - Pre-Compliance (id 0) is only "current" when the sentinel is -1, so it needs
 *   an advance ONLY when backendCurrentStep === -1. This avoids the previous
 *   `backendCurrentStep >= 0` always-true bug that would have made Step 0 think
 *   it was always already-past.
 * - Every other step keeps the existing "already at or past" guard
 *   (backendCurrentStep >= stepId → no advance needed).
 */
export const shouldAdvanceBackendStep = (
	stepId: number,
	backendCurrentStep: number
): boolean => {
	if (stepId === EVALUATION_STEP_IDS.PRE_COMPLIANCE) {
		return backendCurrentStep === -1;
	}
	return backendCurrentStep < stepId;
};

/**
 * Determines whether a step at the given filtered index should be enabled,
 * based on the backend's current step progress (also remapped to filtered index).
 */
const getStepEnabled = (
	filteredStepIndex: number,
	mappedCurrentStep: number,
	evaluationState: EvaluationState | null
): boolean => {
	if (filteredStepIndex === 0) {
		return true;
	}

	if (!evaluationState) {
		return false;
	}

	// Enable steps up to one ahead of current progress so the user can advance
	return filteredStepIndex <= mappedCurrentStep + 1;
};

interface StepConfig {
	id: number;
	element: React.ReactNode;
	meta: typeof EVALUATION_STEP_META[number];
	nextLabel?: string;
	readOnly?: boolean;
	hideNext?: boolean;
	displayNumberOverride?: number | string;
	onBeforeNext?: () => Promise<boolean> | boolean;
}

export const RFxEvaluationSteps: React.FC<RFxEvaluationStepsProps> = ({
	rfxData,
	rfxSubmissionId,
	evaluationState,
	isRfxLead = false,
	onComplete,
	onStepChange,
	onPanelModeChange,
	onAdvanceStep,
	onRefreshUserContext,
}) => {
	const rfxId = rfxData?.id;
	const queryClient = useQueryClient();
	const savedPanelMode = (evaluationState?.panelMode as PanelMode) ?? undefined;
	// Solo workflows must not show panel-centric help text (PRO-467). Default to
	// panel copy when the mode is not yet known.
	const isSoloWorkflow = savedPanelMode ? isSoloPanelMode(savedPanelMode) : false;
	const panelMemberId = evaluationState?.currentUserContext?.panelMemberId;
	const panelMemberStatus = evaluationState?.currentUserContext?.panelMemberStatus;
	const scoringBlockedReason = evaluationState?.currentUserContext?.scoringBlockedReason;
	const criteriaBeforeNextRef = useRef<(() => Promise<boolean>) | null>(null);
	const panelBeforeNextRef = useRef<(() => Promise<boolean>) | null>(null);
	const initialEvalBeforeNextRef = useRef<(() => Promise<boolean>) | null>(null);
	const clarificationsBeforeNextRef = useRef<(() => Promise<boolean>) | null>(null);
	const [initialEvalComplete, setInitialEvalComplete] = useState(false);
	const finalEvalBeforeNextRef = useRef<(() => Promise<boolean>) | null>(null);
	const [finalEvalComplete, setFinalEvalComplete] = useState(false);
	// Stepper-supplied "advance to next visual step" fn. StepContainer registers
	// this; Step 0 (Pre-Compliance) calls it on completion so the UI moves past
	// the Step 0 circle (the internal Proceed/Skip buttons bypass the Next button).
	const goToNextStepRef = useRef<(() => void) | null>(null);

	const registerGoToNextStep = useCallback((goToNextStep: () => void) => {
		goToNextStepRef.current = goToNextStep;
	}, []);

	const handleInitialEvalCompletionChange = useCallback((isComplete: boolean) => {
		setInitialEvalComplete(isComplete);
	}, []);

	const handleFinalEvalCompletionChange = useCallback((isComplete: boolean) => {
		setFinalEvalComplete(isComplete);
	}, []);

	// Determine which step IDs are active based on workflow config.
	// Step 0 (Pre-Compliance) is a frontend-owned step layered on every
	// evaluation workflow, but the backend's workflow_config.steps may not list
	// it (e.g. legacy/seeded configs return [1..7]). Always ensure it is present
	// and first so the Pre-Compliance step renders in both solo and panel modes.
	const activeStepIds = useMemo(() => {
		const steps = evaluationState?.workflowConfig?.steps ?? [...ALL_STEP_IDS];
		return steps.includes(EVALUATION_STEP_IDS.PRE_COMPLIANCE)
			? steps
			: [EVALUATION_STEP_IDS.PRE_COMPLIANCE, ...steps];
	}, [evaluationState?.workflowConfig?.steps]);

	// Read compliance status: current_step drives the sentinel -1 guard (Task 010);
	// locked/skipped drive Step 0's read-only and visual state (Task 011).
	const { data: complianceStatus, isLoading: complianceStatusLoading } =
		useComplianceStatus(rfxId);
	const complianceLocked = complianceStatus?.locked ?? false;
	const complianceSkipped = complianceStatus?.skipped ?? false;
	// Pre-Compliance is resolved once the backend stops reporting the -1 sentinel
	// (i.e. completed via the grid "Proceed", skipped, or locked). Mirrors the
	// backend's isAtPreCompliance() so the Step 0 view shows the completed grid
	// instead of the editable questions setup on Back/refresh (PRO-575).
	const preComplianceResolved =
		complianceStatus !== undefined && complianceStatus.current_step !== -1;

	// The compliance status cache is keyed only by rfxId, but its result depends on
	// the active evaluation (latest eval row). When the lead resets and a new
	// evaluation is created for this RFx, the stale cache would still report the
	// previous run's resolution and wrongly show Step 0 as already completed. Drop
	// the compliance caches whenever the evaluation id changes so the stepper
	// re-reads the fresh -1 sentinel (PRO-575).
	const evaluationId = evaluationState?.id;
	useEffect(() => {
		if (rfxId) {
			// reset (not invalidate) so the status query drops its cached value and
			// goes back to a loading state — this drives the spinner below and avoids
			// the "Pre-Compliance flashes green" bug where a stale completed status
			// from the previous evaluation is shown while the new one loads.
			queryClient.resetQueries({
				queryKey: complianceKeys.root(rfxId),
			});
		}
	}, [evaluationId, rfxId, queryClient]);

	// If the compliance API reports sentinel -1, override backendCurrentStep so
	// mapBackendStepToFilteredIndex routes to Pre-Compliance (Step 0). Otherwise
	// fall back to the evaluation state's currentStep (existing behaviour).
	const backendCurrentStep =
		complianceStatus?.current_step === -1
			? -1
			: (evaluationState?.currentStep ?? 0);
	const mappedCurrentStep = mapBackendStepToFilteredIndex(
		backendCurrentStep,
		activeStepIds
	);
	const highestStep = mappedCurrentStep;
	const initialStep = mappedCurrentStep;

	/**
	 * Only call onAdvanceStep if the backend hasn't already passed this step.
	 * Step ID N is "already past" when backendCurrentStep >= N (0-based DB value).
	 * Returns true if advance succeeded or was unnecessary; false if it failed.
	 */
	const maybeAdvance = useCallback(async (stepId: number): Promise<boolean> => {
		// Guard logic lives in the pure shouldAdvanceBackendStep helper (tested
		// directly). If no advance is needed, the backend is already at/past this
		// step (or, for Pre-Compliance, past the -1 sentinel) — nothing to do.
		if (!shouldAdvanceBackendStep(stepId, backendCurrentStep)) {
			return true;
		}
		if (onAdvanceStep) {
			const result = await onAdvanceStep();
			if (!result) return false;
		}
		return true;
	}, [backendCurrentStep, onAdvanceStep]);

	/**
	 * Step 0 (Pre-Compliance) completion handler. Fired by the grid's onComplete
	 * and the skip-confirm path — neither goes through the stepper Next button, so
	 * we must advance BOTH the backend (maybeAdvance) AND the stepper UI
	 * (goToNextStep) here, otherwise the user is stranded on the Step 0 circle.
	 * Awaits the backend advance and only moves the UI when it succeeded; on
	 * failure maybeAdvance returns false (onAdvanceStep surfaces its own error
	 * via evaluation state) and the user stays on Step 0 to retry.
	 */
	const handlePreComplianceComplete = useCallback(
		async (source: PreComplianceCompletionSource): Promise<void> => {
			// Skip resolves Pre-Compliance entirely server-side (the skip endpoint
			// lands the eval on Criteria and the hook invalidates the compliance
			// cache). Calling advance-step here would re-read current_step=0 as
			// "at Criteria" and overshoot into the next step's criteria validator
			// (500: "At least one evaluation criterion must be defined"). So for the
			// skip path we ONLY move the stepper UI forward.
			if (source === "skip") {
				goToNextStepRef.current?.();
				return;
			}

			// Grid "Proceed" path: the backend has not advanced yet, so advance it
			// (its Pre-Compliance branch sets pre_compliance_completed + lands on
			// Criteria). Only move the UI once that succeeds.
			const advanced = await maybeAdvance(EVALUATION_STEP_IDS.PRE_COMPLIANCE);
			if (advanced) {
				// advance-step flipped the backend off the -1 sentinel, but the
				// status query still holds the stale -1 (it was last fetched before
				// the advance). Refresh it so the Step 0 circle turns complete
				// immediately instead of staying "pending" until a manual refresh.
				queryClient.invalidateQueries({
					queryKey: complianceKeys.status(rfxId),
				});
				goToNextStepRef.current?.();
			}
		},
		[maybeAdvance, queryClient, rfxId]
	);

	// Build all step configurations with their IDs (id=0 is Pre-Compliance).
	// NOTE: each config's `meta` is taken from EVALUATION_STEP_META by ARRAY INDEX
	// (not by `.id`). Since Pre-Compliance is now prepended at index 0, every
	// existing entry's index is shifted +1 (Criteria=[1] … Final=[7]).
	const allStepConfigs: StepConfig[] = useMemo(() => [
		{
			id: EVALUATION_STEP_IDS.PRE_COMPLIANCE,
			meta: EVALUATION_STEP_META[0],
			displayNumberOverride: 0,
			// When the grid is locked (compliance already finalised) or the step was
			// skipped, render it read-only so users can review but not re-submit.
			readOnly: complianceLocked || complianceSkipped,
			// Pre-Compliance has no stepper "Next" button — progression is driven
			// entirely by its own internal "Proceed" / "Skip" controls (which call
			// onStepComplete). A generic Next would let the lead bypass those gates.
			hideNext: true,
			element: (
				<PreComplianceStep
					rfxId={rfxId}
					// Already resolved → open the completed grid, not the setup.
					completed={preComplianceResolved}
					// Skipped → show the skipped summary with "Start Compliance".
					skipped={complianceSkipped}
					// Internal completion (skip-confirm / grid Proceed) bypasses the
					// Next button, so this advances both backend and stepper UI.
					onStepComplete={(source) => {
						void handlePreComplianceComplete(source);
					}}
					renderStage2={(props) => (
						<Stage2ComplianceGrid {...props} />
					)}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.CRITERIA,
			meta: EVALUATION_STEP_META[1],
			// NOTE: the `2` here is a 0-based BACKEND step index (current_step),
			// NOT a step ID. It means "scoring has started" and is unrelated to the
			// Pre-Compliance prepend (whose sentinel is -1, handled in maybeAdvance).
			readOnly: !isRfxLead || backendCurrentStep >= 2,
			onBeforeNext: async () => {
				if (criteriaBeforeNextRef.current) {
					const valid = await criteriaBeforeNextRef.current();
					if (!valid) return false;
				}
				return maybeAdvance(EVALUATION_STEP_IDS.CRITERIA);
			},
			element: (
				<EvaluationCriteriaStep
					rfxId={rfxId}
					rfxSubmissionId={rfxSubmissionId}
					isRfxLead={isRfxLead}
					isLocked={
						(savedPanelMode === "manual_panel" || savedPanelMode === "ai_panel") &&
						backendCurrentStep >= 2
					}
					onBeforeNext={(check) => {
						criteriaBeforeNextRef.current = check;
					}}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.PANEL,
			meta: EVALUATION_STEP_META[2],
			readOnly: !isRfxLead || backendCurrentStep >= 3,
			nextLabel: backendCurrentStep >= 2 ? "Continue Evaluation" : "Start Evaluation",
			onBeforeNext: async () => {
				if (panelBeforeNextRef.current) {
					const valid = await panelBeforeNextRef.current();
					if (!valid) {
						return false;
					}
				}
				// Advance backend step — ensures current_step >= 2 before
				// InitialEvaluationStep renders and hits scoring endpoints
				return maybeAdvance(EVALUATION_STEP_IDS.PANEL);
			},
			element: (
				<EvaluationPanelStep
					rfxData={rfxData}
					evalId={evaluationState?.id}
					isRfxLead={isRfxLead}
					disclaimerEnabled={evaluationState?.disclaimerEnabled ?? false}
					savedPanelMode={savedPanelMode}
					onPanelModeChange={onPanelModeChange}
					onBeforeNext={(check) => {
						panelBeforeNextRef.current = check;
					}}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.INITIAL,
			meta: EVALUATION_STEP_META[3],
			readOnly: !isRfxLead || backendCurrentStep >= 3,
			nextLabel: "Proceed to Clarification Review",
			onBeforeNext: async () => {
				if (initialEvalBeforeNextRef.current) {
					const valid = await initialEvalBeforeNextRef.current();
					if (!valid) return false;
				}
				return maybeAdvance(EVALUATION_STEP_IDS.INITIAL);
			},
			element: (
				<InitialEvaluationStep
					rfxData={rfxData}
					evalId={evaluationState?.id}
					currentStep={evaluationState?.currentStep}
					disclaimerEnabled={evaluationState?.disclaimerEnabled ?? false}
					panelMode={savedPanelMode}
					panelMemberId={panelMemberId ?? undefined}
					panelMemberStatus={panelMemberStatus ?? undefined}
					scoringBlockedReason={scoringBlockedReason ?? undefined}
					onBeforeNext={(check) => {
						initialEvalBeforeNextRef.current = check;
					}}
					onCompletionChange={handleInitialEvalCompletionChange}
					onRefreshUserContext={onRefreshUserContext}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.CLARIFICATIONS,
			meta: EVALUATION_STEP_META[4],
			readOnly: !isRfxLead,
			onBeforeNext: async () => {
				if (clarificationsBeforeNextRef.current) {
					const valid = await clarificationsBeforeNextRef.current();
					if (!valid) return false;
				}
				return maybeAdvance(EVALUATION_STEP_IDS.CLARIFICATIONS);
			},
			element: (
				<ClarificationsStep
					rfxId={rfxId}
					rfxSubmissionId={rfxSubmissionId}
					evalId={evaluationState?.id}
					isRfxLead={isRfxLead}
					panelMode={savedPanelMode}
					onBeforeNext={(check) => {
						clarificationsBeforeNextRef.current = check;
					}}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.REVISIONS,
			meta: EVALUATION_STEP_META[5],
			onBeforeNext: async () => {
				return maybeAdvance(EVALUATION_STEP_IDS.REVISIONS);
			},
			element: (
				<RevisionsStep
					rfxId={rfxId}
					evaluationId={evaluationState?.id}
					rfxSubmissionId={rfxSubmissionId}
					isRfxLead={isRfxLead}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.PANEL_MEETING,
			meta: EVALUATION_STEP_META[6],
			readOnly: !isRfxLead,
			onBeforeNext: async () => {
				if (finalEvalBeforeNextRef.current) {
					const valid = await finalEvalBeforeNextRef.current();
					if (!valid) return false;
				}
				return maybeAdvance(EVALUATION_STEP_IDS.PANEL_MEETING);
			},
			element: (
				<FinalEvaluationStep
					rfxData={rfxData}
					evalId={evaluationState?.id}
					currentStep={evaluationState?.currentStep}
					panelMode={savedPanelMode}
					panelMemberId={panelMemberId ?? undefined}
					panelMemberStatus={panelMemberStatus ?? undefined}
					onBeforeNext={(check) => {
						finalEvalBeforeNextRef.current = check;
					}}
					onCompletionChange={handleFinalEvalCompletionChange}
					onRefreshUserContext={onRefreshUserContext}
				/>
			),
		},
		{
			id: EVALUATION_STEP_IDS.FINAL,
			meta: EVALUATION_STEP_META[7],
			element: (
				<FinalReportAndAwardStep
					rfxId={rfxId}
					rfxSubmissionId={rfxSubmissionId}
					evalId={evaluationState?.id}
					isRfxLead={isRfxLead}
				/>
			),
		},
	], [
		rfxId,
		rfxSubmissionId,
		rfxData,
		evaluationState?.id,
		evaluationState?.currentStep,
		evaluationState?.disclaimerEnabled,
		isRfxLead,
		savedPanelMode,
		panelMemberId,
		panelMemberStatus,
		scoringBlockedReason,
		backendCurrentStep,
		complianceLocked,
		complianceSkipped,
		preComplianceResolved,
		maybeAdvance,
		handlePreComplianceComplete,
		onPanelModeChange,
		onRefreshUserContext,
		handleInitialEvalCompletionChange,
		handleFinalEvalCompletionChange,
	]);

	// Filter to only active steps. Number them by their 0-based position so the
	// prepended Step 0 (Pre-Compliance) shows "0" and every following step keeps
	// the number it had before Step 0 existed (Criteria "1", Panel "2", …) rather
	// than the default index+1, which would shift them all up by one.
	const visibleSteps = useMemo(() => {
		return allStepConfigs
			.filter((step) => activeStepIds.includes(step.id))
			.map((step, index) => ({ ...step, displayNumberOverride: index }));
	}, [allStepConfigs, activeStepIds]);

	// Hold the stepper back until the compliance status is known. Rendering early
	// would briefly place the user at evaluationState.currentStep (Criteria),
	// making the prepended Pre-Compliance circle flash green before the -1
	// sentinel arrives. Show a spinner while everything boots up instead (PRO-575).
	if (complianceStatusLoading) {
		return (
			<div
				className="flex justify-center py-16"
				data-testid="evaluation-steps-loading"
			>
				<Spinner data-testid="evaluation-steps-spinner" />
			</div>
		);
	}

	return (
		<StepContainer
			initialStep={initialStep}
			highestStep={highestStep}
			onComplete={onComplete}
			onStepChange={onStepChange}
			registerGoToNextStep={registerGoToNextStep}
			showLegend={false}
		>
			{visibleSteps.map((step, filteredIndex) => {
				const isInitialEvalStep = step.id === EVALUATION_STEP_IDS.INITIAL;
				const isFinalEvalStep = step.id === EVALUATION_STEP_IDS.FINAL;
				const stepEnabled = getStepEnabled(filteredIndex, mappedCurrentStep, evaluationState);

				let enabled = stepEnabled;
				if (isInitialEvalStep) {
					enabled = stepEnabled && initialEvalComplete;
				} else if (isFinalEvalStep) {
					enabled = stepEnabled && finalEvalComplete;
				}

				return (
					<Step
						key={step.id}
						name={step.meta.name}
						description={resolveStepDescription(step.meta.description, isSoloWorkflow)}
						enabled={enabled}
						readOnly={step.readOnly}
						hideNext={step.hideNext}
						nextLabel={step.nextLabel}
						displayNumberOverride={step.displayNumberOverride}
						onBeforeNext={step.onBeforeNext}
					>
						{step.element}
					</Step>
				);
			})}
		</StepContainer>
	);
};
